<template>
  <div class="flex flex-col items-center gap-[12px] w-full">
    <div class="pb-[12px] w-full">
      <h2 class="text-[28px] max-md:text-[24px] font-bold text-[#3a3d44] text-center leading-[1.3]">
        Вход через email
      </h2>
    </div>

    <!-- Ошибка: почта не найдена -->
    <p v-if="emailNotFound" class="text-[#f8465b] text-base text-center">
      Такой почты мы не нашли!
    </p>

    <form class="flex flex-col items-center gap-[12px] w-full" @submit.prevent="handleSubmit">
      <UINewInput
        v-model="emailValue"
        placeholder="Email"
        type="email"
        class="!w-[368px] max-md:!w-full"
        input-classes="!rounded-[12px] !border-[#d9e5ff] w-full"
        :validation-rules="[
          { type: 'required', message: 'Введите email' },
          { type: 'email', message: 'Некорректный email' },
        ]"
        @update:validation-error="emailError = $event"
      />

      <!-- Пароль (скрываем когда почта не найдена) -->
      <UINewInput
        v-if="!emailNotFound"
        v-model="password"
        placeholder="Введите пароль"
        :type="showPassword ? 'text' : 'password'"
        class="!w-[368px] max-md:!w-full"
        input-classes="!rounded-[12px] !border-[#d9e5ff] w-full"
        :validation-rules="[
          { type: 'required', message: 'Введите пароль' },
        ]"
        @update:validation-error="passwordError = $event"
      >
        <template #postfix>
          <div class="flex items-center justify-center p-1 cursor-pointer" @click="showPassword = !showPassword">
            <i class="fi fi-sr-eye text-xl flex" :class="showPassword ? 'text-primary' : 'text-primary/40'" />
          </div>
        </template>
      </UINewInput>

      <p v-if="serverError && !passwordError" class="text-[#f8465b] text-sm">{{ serverError }}</p>

      <!-- Кнопка: "Войти" или "Создать аккаунт" -->
      <button
        type="submit"
        class="bg-primary rounded-[12px] py-3 w-[368px] max-md:w-full text-white font-bold text-base disabled:opacity-50"
        :disabled="submitDisabled"
      >
        <span v-if="loading" class="loader-inline mr-2" />
        {{ emailNotFound ? 'Создать аккаунт' : 'Войти' }}
      </button>
    </form>

    <!-- Назад -->
    <div class="flex items-center justify-center">
      <span class="text-primary text-sm cursor-pointer" @click="handleBack">
        Назад
      </span>
    </div>

    <!-- Забыли пароль? Восстановить (только в режиме входа) -->
    <div v-if="!emailNotFound" class="flex items-center justify-center gap-1.5 text-sm text-[#3A3D44]/60">
      Забыли пароль?
      <span class="text-primary cursor-pointer" @click="$emit('setMode', LoginMode.RECOVERY_EMAIL)">
        Восстановить
      </span>
    </div>

    <!-- Юридический текст -->
    <p class="font-normal text-xs text-center text-[rgba(58,61,68,0.6)] pt-[12px] w-[350px] max-md:w-full max-md:px-4">
      Продолжая, вы соглашаетесь с
      <a href="/pdf/oferta.pdf" target="_blank" class="underline !text-[rgba(58,61,68,0.6)] !font-normal">офертой</a>
      и даёте
      <a href="/pdf/consent.pdf" target="_blank" class="underline !text-[rgba(58,61,68,0.6)] !font-normal">согласие</a>
      на обработку данных в соответствии с
      <a href="/pdf/politic.pdf" target="_blank" class="underline !text-[rgba(58,61,68,0.6)] !font-normal">политикой в отношении обработки персональных данных</a>
    </p>
  </div>
</template>

<script setup lang="ts">
import { LoginMode, LoginScreenContext } from '~/interfaces/login/UI';
import { ApiKey } from '~/common/API/baseApi';
import { useUserStore } from '~/store/UserModule';
import AuthService from '~/services/AuthService';

const emit = defineEmits<{
  (e: 'setMode', mode: LoginMode): void;
  (e: 'setEmail', email: string): void;
  (e: 'setRegistrationId', id: number): void;
  (e: 'setExpiredAt', expiredAt: string): void;
  (e: 'setPassword', password: string): void;
  (e: 'authorized'): void;
}>();

const props = defineProps<{
  email: string;
  context: LoginScreenContext;
}>();

const emailValue = ref(props.email);
const emailError = ref('');
const password = ref('');
const passwordError = ref('');
const serverError = ref('');
const loading = ref(false);
const showPassword = ref(false);
const emailNotFound = ref(false);

// При изменении email сбрасываем ошибку "почта не найдена"
watch(emailValue, () => {
  if (emailNotFound.value) {
    emailNotFound.value = false;
    serverError.value = '';
  }
});

// При вводе пароля сбрасываем серверную ошибку
watch(password, () => {
  serverError.value = '';
});

const handleBack = () => {
  if (emailNotFound.value) {
    emailNotFound.value = false;
    serverError.value = '';
  } else {
    emit('setMode', LoginMode.EMAIL_CHOICE);
  }
};

const submitDisabled = computed(() => {
  if (loading.value || !!emailError.value || !emailValue.value) return true;
  if (!emailNotFound.value && (!!passwordError.value || !password.value)) return true;
  return false;
});

const handleSubmit = async () => {
  if (submitDisabled.value) return;

  loading.value = true;
  serverError.value = '';

  // Почта не найдена → переход на регистрацию
  if (emailNotFound.value) {
    emit('setEmail', emailValue.value);
    emit('setMode', LoginMode.REGISTER_EMAIL);
    loading.value = false;
    return;
  }

  // Режим входа
  try {
    // Проверяем, существует ли email
    const { available } = await AuthService.checkEmail(emailValue.value);
    if (available) {
      // Почта не найдена
      emailNotFound.value = true;
      loading.value = false;
      return;
    }

    // Email существует — пробуем залогиниться
    const { id, accessToken } = await AuthService.loginEmail(emailValue.value, password.value);
    ApiKey.set(accessToken);
    useCookie("authorizedP").value = accessToken;
    const userStore = useUserStore();
    userStore.setAuthorized('', accessToken, String(id));
    userStore.loginDialogVisible = false;
    if (props.context === LoginScreenContext.null) {
      window.location.reload();
    } else {
      emit('authorized');
    }
  } catch {
    serverError.value = 'Неверный пароль!';
  } finally {
    loading.value = false;
  }
};
</script>
